// Long-form founder login + landing for the 1to10 Founder Edge OS.
// Mirrors the structure of the investor login (hero, value props, features,
// journey, stats, then the actual sign-in form anchored at the bottom).
// Voice: blunt plain Indian English, no em dashes, no metaphors, numerals
// as digits. Existing auth logic at the top is unchanged so dummy@dummy.in
// and the must_change_password flow still work.
import { useState, useRef } from "react";
import { useLocation } from "wouter";
import {
  Eye, EyeOff, Loader2, Lock, Mail, ArrowRight, ChevronDown,
  MessageSquare, ShieldCheck, FileCheck2, BarChart3, FileText,
  Eye as EyeIcon, Sparkles, Users, Target, Zap, Star,
  CheckCircle2, Calendar, TrendingUp,
} from "lucide-react";
import { ForgotPasswordModal } from "@/pages/auth/forgot-password-modal";
import { useCohort, fp } from "@/lib/founder-cohort";

const BASE = import.meta.env.BASE_URL;

export default function FounderLogin() {
  const [, navigate] = useLocation();
  const { slug, theme } = useCohort();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  const loginRef = useRef<HTMLDivElement>(null);

  const scrollToLogin = () => loginRef.current?.scrollIntoView({ behavior: "smooth" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await fetch(`${BASE}api/founders/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password, cohortSlug: slug }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error || "Login failed");
        setLoading(false);
        return;
      }
      localStorage.setItem("founder_token", data.token);
      localStorage.setItem("founder_id", String(data.founder.id));
      localStorage.setItem("founder_name", data.founder.name || "");
      localStorage.setItem("founder_company_id", String(data.founder.companyId));
      localStorage.setItem("founder_company_name", data.founder.companyName || "");
      if (data.mustChangePassword) {
        localStorage.setItem("founder_must_change_password", "true");
        navigate(fp(slug, "change-password"));
      } else {
        localStorage.removeItem("founder_must_change_password");
        navigate(fp(slug));
      }
    } catch (err: any) {
      setError(err?.message || "Network error");
      setLoading(false);
    }
  };

  // Compact form for cohorts that haven't been given the longform marketing
  // treatment yet. Same auth, same data, different shell.
  if (theme.loginStyle === "compact") {
    return (
      <CompactFounderLogin
        slug={slug}
        theme={theme}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        showPassword={showPassword}
        setShowPassword={setShowPassword}
        loading={loading}
        error={error}
        forgotOpen={forgotOpen}
        setForgotOpen={setForgotOpen}
        onSubmit={handleSubmit}
      />
    );
  }

  const features = [
    {
      icon: BarChart3,
      title: "Command Center",
      desc: "1 screen. Your readiness score, this week's mission, open advisor replies, claim review queue, and the next investor update. No tab hopping.",
    },
    {
      icon: MessageSquare,
      title: "Advisor Thread",
      desc: "1 conversation with Favcy that never resets. Ask anything. Sia drafts a reply. Your assigned advisor approves or corrects it. You always know who said what and when.",
    },
    {
      icon: ShieldCheck,
      title: "Review Room",
      desc: "9 founder claims (revenue, traction, team, IP, etc) reviewed by Favcy before they go on your investor page. Accepted, corrected, or admin-published. No claim ships without sign off.",
    },
    {
      icon: FileCheck2,
      title: "Proof Engine",
      desc: "Every number on your investor page is backed by a link, a screenshot or a signed document. If proof is missing, the claim is held back. This is what builds investor trust.",
    },
    {
      icon: TrendingUp,
      title: "Readiness Score",
      desc: "1 number from 0 to 100 that tells you exactly how investor-ready you are this week. Breaks down across product, revenue, team, governance, and proof. Updates as you ship.",
    },
    {
      icon: FileText,
      title: "Weekly Update",
      desc: "Answer 5 questions on Friday. Sia drafts your investor update in your voice. You approve, advisor approves, Favcy publishes. Your investors get a clean update every week without you writing one.",
    },
    {
      icon: EyeIcon,
      title: "Investor Preview",
      desc: "See exactly what an investor sees on your page right now. Everything published, nothing hidden. The same page that goes out to the Favcy Select investor base.",
    },
    {
      icon: Target,
      title: "Missions and Network",
      desc: "Specific intros, hires and asks routed straight to Favcy. No Whatsapp chase. Tracked in the same advisor thread so nothing falls through.",
    },
    {
      icon: Sparkles,
      title: "Sia, your AI co-pilot",
      desc: "Sia (Anthropic powered) drafts updates, structures messy thoughts and writes investor copy in your voice. She drafts. Favcy advisors check. You stay in control.",
    },
  ];

  const surfaces = [
    { n: "1", title: "You signed with Favcy", desc: "Term sheet done. You are part of the cohort." },
    { n: "10", title: "You are Series A ready", desc: "Real revenue. Real proof. Real investor page. Real introductions." },
  ];

  return (
    <div className={/* ALLOW_HARDCODED_COLOR: dark anchor remapped by [data-founder-theme="light"] */ "min-h-screen bg-[#0A0A0A] text-white overflow-y-auto"}>
      <div className="fixed inset-0 pointer-events-none opacity-[0.07]" style={{
        backgroundImage:
          "radial-gradient(circle at 20% 30%, #FF5C00 0%, transparent 50%), radial-gradient(circle at 80% 70%, #FF5C00 0%, transparent 40%), radial-gradient(circle at 50% 50%, #FF5C00 0%, transparent 60%)",
      }} />

      <div className="relative">
        {/* Sticky nav */}
        {/* ALLOW_HARDCODED_COLOR: dark-surface anchor remapped by [data-founder-theme="light"] */}
        <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#0A0A0A]/80 border-b border-white/[0.08]">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
                <span className="text-white font-bold text-lg font-serif">F</span>
              </div>
              <div>
                <p className="text-white font-serif font-bold text-lg leading-none">{theme.brandName}</p>
                <p className="text-white/60 text-[9px] font-mono uppercase tracking-[0.2em]">Founder Edge OS · {theme.shortLabel}</p>
              </div>
            </div>
            <button
              onClick={scrollToLogin}
              data-testid="button-nav-signin"
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Sign In
            </button>
          </div>
        </nav>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white/[0.06] border border-white/15 rounded-full px-4 py-1.5 mb-8">
            <Star className="w-3.5 h-3.5 text-[#FF5C00]" />
            <span className="text-white/70 text-xs font-medium">Built for the Favcy Select cohort. Day 1 to Series A ready.</span>
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tight leading-[1.1] max-w-4xl mx-auto">
            From <span className="text-[#FF5C00]">1</span> to <span className="text-[#FF5C00]">10</span>, with Favcy in the room.
          </h1>
          <p className="text-white/70 text-lg md:text-xl mt-6 max-w-2xl mx-auto leading-relaxed">
            Founder Edge OS is your one screen for everything Favcy is doing for your company this week. Advisor replies, claim reviews, proof, readiness, weekly updates and your investor page. All in one place.
          </p>
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={scrollToLogin}
              data-testid="button-hero-signin"
              className="bg-[#FF5C00] hover:bg-[#E55200] text-white px-8 py-3.5 rounded-xl text-sm font-semibold transition-colors flex items-center gap-2"
            >
              Open My Command Center <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href="mailto:pc@favcy.in?subject=I%20want%20Favcy%20Select%20for%20my%20company"
              className="border border-white/20 hover:border-white/40 text-white/80 hover:text-white px-8 py-3.5 rounded-xl text-sm font-medium transition-all"
            >
              Talk to Favcy
            </a>
          </div>
          <button onClick={scrollToLogin} className="mt-16 animate-bounce text-white/60 hover:text-white/60 transition-colors">
            <ChevronDown className="w-6 h-6" />
          </button>
        </section>

        {/* Why 1 to 10 */}
        <section className="border-y border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-5xl mx-auto px-6 py-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">Why 1 to 10</p>
                <h2 className="text-3xl md:text-4xl font-serif font-bold leading-tight">
                  0 to 1 is your job.<br />
                  <span className="text-[#FF5C00]">1 to 10 is ours.</span>
                </h2>
                <p className="text-white/65 text-sm mt-5 leading-relaxed max-w-md">
                  You found the problem. You shipped a first version. Now the hard part. Going from 1 paying customer to 10. From a deck to a real investor page. From hustle to a story investors actually trust.
                </p>
                <p className="text-white/65 text-sm mt-3 leading-relaxed max-w-md">
                  Favcy Select sits inside this stretch with you. We do not coach in slack threads and forget. Every reply, every approval, every claim, every weekly update is logged in your OS. You always know what is open and what is closed.
                </p>
              </div>
              <div className="space-y-4">
                {surfaces.map((s, i) => (
                  <div key={i} className={`bg-white/[0.04] border rounded-xl p-5 flex items-start gap-4 ${i === 1 ? "border-[#FF5C00]/25" : "border-white/[0.1]"}`}>
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 font-serif font-bold text-lg ${i === 1 ? "bg-[#FF5C00]/15 border border-[#FF5C00]/25 text-[#FF5C00]" : "bg-emerald-500/15 border border-emerald-500/20 text-emerald-400"}`}>
                      {s.n}
                    </div>
                    <div>
                      <p className="text-white font-medium text-sm">{s.title}</p>
                      <p className="text-white/60 text-xs mt-1 leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
                <div className="bg-white/[0.02] border border-dashed border-white/[0.12] rounded-xl p-4 text-center">
                  <p className="text-white/60 text-[11px]">Founder Edge OS is what holds the 1 to 10 journey together.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features grid */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">What you get inside</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">9 surfaces. 1 OS.</h2>
            <p className="text-white/60 text-sm mt-3 max-w-xl mx-auto">
              Every Favcy output for your company lives in one of these surfaces. No spreadsheets. No lost slack. No "I will check and revert."
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {features.map((f, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-6 hover:bg-white/[0.06] hover:border-white/15 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-[#FF5C00]/15 border border-[#FF5C00]/20 flex items-center justify-center mb-4 group-hover:bg-[#FF5C00]/20 transition-colors">
                  <f.icon className="w-5 h-5 text-[#FF5C00]" />
                </div>
                <h3 className="text-white font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-white/60 text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Weekly cadence */}
        <section className="border-y border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-6xl mx-auto px-6 py-20">
            <div className="text-center mb-14">
              <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">The weekly rhythm</p>
              <h2 className="text-3xl md:text-4xl font-serif font-bold">5 days. 1 clean cycle.</h2>
              <p className="text-white/60 text-sm mt-3 max-w-lg mx-auto">
                You do not need to log in every day. The OS is built around 1 weekly cadence so you stay in build mode the rest of the time.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-5 max-w-5xl mx-auto">
              {[
                { day: "Monday", icon: Target, title: "This week's mission", desc: "Favcy posts the 1 thing that will move your readiness score the most this week." },
                { day: "Wednesday", icon: MessageSquare, title: "Advisor check in", desc: "Reply in the thread. Sia drafts. Your advisor signs off. Decisions get logged." },
                { day: "Friday", icon: FileText, title: "Weekly update", desc: "Answer 5 questions. Sia stitches the draft. You approve. Investor update goes out Monday." },
                { day: "Anytime", icon: Zap, title: "Asks and intros", desc: "Need a hire, an intro, a customer connect? Drop it in Ask Favcy. Tracked, not ignored." },
              ].map((s, i) => (
                <div key={i} className="bg-white/[0.04] border border-white/[0.12] rounded-2xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Calendar className="w-3.5 h-3.5 text-[#FF5C00]" />
                    <p className="text-[#FF5C00] text-[10px] uppercase tracking-wider font-semibold">{s.day}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/[0.06] border border-white/[0.12] flex items-center justify-center mb-3">
                    <s.icon className="w-5 h-5 text-white/80" />
                  </div>
                  <p className="text-white font-semibold text-sm">{s.title}</p>
                  <p className="text-white/60 text-xs mt-2 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why this is different */}
        <section className="max-w-5xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <p className="text-[#FF5C00] text-xs font-semibold uppercase tracking-widest mb-3">Why founders pick this over a coach</p>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">No coach gives you an investor page.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              "Every reply you get is from a real Favcy advisor, by name, with a timestamp. Not a faceless inbox.",
              "Your investor page is built from your own claims, reviewed by Favcy, backed by your own proof. Not a templated 1 pager.",
              "Sia drafts in your voice. Plain Indian English. No GPT lingo. No metaphors. You sound like you, not like a chatbot.",
              "Weekly update goes out without you fighting Notion at 11pm on Friday. 5 questions. Done.",
              "Readiness score is honest. If you are not Series A ready, it tells you why and what to fix this week.",
              "Everything is auditable. Open Settings and download every reply, claim, draft and update. It is your data.",
            ].map((line, i) => (
              <div key={i} className="bg-white/[0.04] border border-white/[0.1] rounded-xl p-5 flex items-start gap-3">
                <CheckCircle2 className="w-4 h-4 text-[#FF5C00] mt-0.5 flex-shrink-0" />
                <p className="text-white/75 text-sm leading-relaxed">{line}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Stats */}
        <section className="max-w-6xl mx-auto px-6 pb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {[
              { value: "9", label: "Founder claims reviewed" },
              { value: "1", label: "Investor page per company" },
              { value: "5", label: "Questions per weekly update" },
              { value: "0", label: "Lost messages" },
            ].map((stat, i) => (
              <div key={i} className="text-center py-4">
                <p className="text-3xl md:text-4xl font-bold font-mono text-[#FF5C00]">{stat.value}</p>
                <p className="text-white/60 text-xs mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Sign in */}
        <section ref={loginRef} className="border-t border-white/[0.08] bg-white/[0.02]">
          <div className="max-w-md mx-auto px-6 py-20">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] mb-4">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-serif font-bold">Welcome back, founder.</h2>
              <p className="text-white/60 text-sm mt-2">Your command center is one login away.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 bg-white/[0.04] border border-white/[0.12] rounded-2xl p-8">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    autoFocus
                    data-testid="input-email"
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF5C00]/60 focus:ring-1 focus:ring-[#FF5C00]/40"
                    placeholder="you@yourcompany.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    data-testid="input-password"
                    className="w-full bg-white/[0.06] border border-white/15 rounded-lg pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#FF5C00]/60 focus:ring-1 focus:ring-[#FF5C00]/40"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/70"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">{error}</div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid="button-login"
                className="w-full bg-gradient-to-r from-[#FF5C00] to-[#E55200] hover:opacity-90 disabled:opacity-50 text-white rounded-lg py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-opacity"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
              </button>

              <div className="text-center text-xs text-white/60 pt-1">
                <button
                  type="button"
                  onClick={() => setForgotOpen(true)}
                  data-testid="link-forgot-password"
                  className="text-[#FF5C00] hover:underline font-medium"
                >
                  Forgot your password?
                </button>
                <span className="text-white/60 mx-2">·</span>
                Stuck? Contact your {theme.supportContact}.
              </div>
            </form>

            <div className="mt-6 flex items-center justify-center gap-2 text-white/60 text-xs">
              <Users className="w-3 h-3" />
              <span>For founders in the Favcy Select cohort only</span>
            </div>
          </div>
        </section>

        {/* Footer */}
        {/* ALLOW_HARDCODED_COLOR: dark-surface anchor remapped by [data-founder-theme="light"] */}
        <footer className="border-t border-white/[0.08] bg-[#0A0A0A]">
          <div className="max-w-6xl mx-auto px-6 py-10">
            <div className="text-[11px] text-white/60 leading-relaxed space-y-3 max-w-3xl">
              <p>
                Founder Edge OS is a private workspace for founders in the Favcy Select cohort. If you are stuck, please contact your {theme.supportContact}.
              </p>
              <p>
                Sia drafts replies and updates using AI. A Favcy advisor reviews everything before it goes out to investors. Nothing is auto-published to your investor page without human sign off.
              </p>
            </div>
            <div className="mt-8 pt-6 border-t border-white/[0.06] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-[11px] text-white/60">&copy; {new Date().getFullYear()} Favcy Venture Builders Private Limited. All rights reserved.</p>
              <p className="text-[11px] text-white/60">{theme.footerLabel}</p>
            </div>
          </div>
        </footer>
      </div>

      <ForgotPasswordModal audience="founder" open={forgotOpen} initialEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}

// Compact, no-marketing variant. Used for cohorts that haven't been given
// program-specific copy yet. Same auth wiring, just no scroll-y hero.
function CompactFounderLogin(props: {
  slug: string;
  theme: import("@/config/cohort-themes").CohortTheme;
  email: string;
  setEmail: (s: string) => void;
  password: string;
  setPassword: (s: string) => void;
  showPassword: boolean;
  setShowPassword: (b: boolean) => void;
  loading: boolean;
  error: string | null;
  forgotOpen: boolean;
  setForgotOpen: (b: boolean) => void;
  onSubmit: (e: React.FormEvent) => Promise<void>;
}) {
  const { theme, email, setEmail, password, setPassword, showPassword, setShowPassword, loading, error, forgotOpen, setForgotOpen, onSubmit } = props;
  return (
    <div className={/* ALLOW_HARDCODED_COLOR: dark anchor remapped by [data-founder-theme="light"] */ "min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6 py-12"}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF5C00] to-[#E55200] flex items-center justify-center">
              <span className="text-white font-bold text-xl font-serif">F</span>
            </div>
            <div className="text-left">
              <p className="text-white font-serif font-bold text-lg leading-none">{theme.brandName}</p>
              <p className="text-white/60 text-[9px] font-mono uppercase tracking-[0.2em] mt-1">{theme.programLabel}</p>
            </div>
          </div>
          <h1 className="text-2xl font-serif font-bold tracking-tight">Welcome back, founder.</h1>
          <p className="text-white/60 text-sm mt-2">Sign in to your {theme.shortLabel} workspace.</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5 bg-white/[0.04] border border-white/[0.12] rounded-2xl p-7">
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                autoFocus
                data-testid="input-email"
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg pl-10 pr-3 py-3 text-sm focus:outline-none focus:border-[#FF5C00]/60 focus:ring-1 focus:ring-[#FF5C00]/40"
                placeholder="you@yourcompany.com"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-white/60 mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                data-testid="input-password"
                className="w-full bg-white/[0.06] border border-white/15 rounded-lg pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-[#FF5C00]/60 focus:ring-1 focus:ring-[#FF5C00]/40"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white/70"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="button-login"
            className="w-full bg-gradient-to-r from-[#FF5C00] to-[#E55200] hover:opacity-90 disabled:opacity-50 text-white rounded-lg py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-opacity"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Signing in...</> : <>Sign in <ArrowRight className="w-4 h-4" /></>}
          </button>

          <div className="text-center text-xs text-white/60 pt-1">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              data-testid="link-forgot-password"
              className="text-[#FF5C00] hover:underline font-medium"
            >
              Forgot your password?
            </button>
            <span className="text-white/60 mx-2">·</span>
            Stuck? Contact your {theme.supportContact}.
          </div>
        </form>

        <div className="mt-6 text-center text-[11px] text-white/60">
          {theme.footerLabel}
        </div>
      </div>

      <ForgotPasswordModal audience="founder" open={forgotOpen} initialEmail={email} onClose={() => setForgotOpen(false)} />
    </div>
  );
}
